import { useState, useEffect, useCallback } from "react";
import {
  DataGrid,
  gridPageCountSelector,
  gridPageSizeSelector,
  gridPageSelector,
  useGridApiRef,
  useGridApiContext,
  useGridSelector,
} from "@mui/x-data-grid";
import { Box, Typography, Select, MenuItem } from "@mui/material";
import MuiPagination from "@mui/material/Pagination";
import * as styles from "./Styles.js";

const autosizeOptions = {
  includeOutliers: true,
  includeHeaders: true,
  expand: true,
};

// Backwards compatible defaults: unless a caller passes `pageSizeOptions` /
// `initialPageSize`, this keeps behaving exactly like it did before (10 rows
// per page, 5/10 as the only page size choices).
const DEFAULT_PAGE_SIZE_OPTIONS = [5, 10];
const DEFAULT_PAGE_SIZE = 10;

// Reserve enough room for the "no rows" overlay even though the grid
// otherwise sizes itself to its content (via `autoHeight`) - without this,
// an empty grid can collapse down to just its header row.
const EMPTY_STATE_MIN_HEIGHT = 220;

function TableComponent(props) {
  const [totalAmount, SetTotalAmount] = useState();
  const apiRef = useGridApiRef();

  const pageSizeOptions = props.pageSizeOptions || DEFAULT_PAGE_SIZE_OPTIONS;
  const initialPageSize = props.initialPageSize || DEFAULT_PAGE_SIZE;
  const totalRowCount = Array.isArray(props.rows) ? props.rows.length : 0;

  const handleResize = useCallback(() => {
    if (apiRef.current) {
      apiRef.current.autosizeColumns(autosizeOptions);
    }
  }, [apiRef]);

  useEffect(() => {
    const resizeListener = () => {
      window.requestAnimationFrame(() => {
        handleResize();
      });
    };

    resizeListener();
    window.addEventListener("resize", resizeListener);
    return () => {
      window.removeEventListener("resize", resizeListener);
    };
  }, [handleResize]);

  useEffect(() => {
    const frameId = window.requestAnimationFrame(() => {
      handleResize();
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [handleResize, props.rows, props.columns]);

  useEffect(() => {
    SetTotalAmount(props.TotalAmount);
  }, [props.TotalAmount]);

  // Fully custom footer - replaces the default `GridPagination` (built on
  // top of MUI's `TablePagination`) entirely, instead of trying to hide/
  // rearrange pieces of it. That approach previously caused the "Rows per
  // page" selector and the row-count label to visually overlap each other.
  //
  // Laid out as a 3-column grid (instead of a plain flex row) so the
  // pagination buttons stay truly centered regardless of how wide the
  // "Rows per page" selector or the "Total" label happen to be:
  //   - left column: the rows-per-page selector
  //   - center column: the page buttons (centered)
  //   - right column: total records (+ optional total amount)
  function CustomFooter() {
    const apiRefInner = useGridApiContext();
    const page = useGridSelector(apiRefInner, gridPageSelector);
    const pageCount = useGridSelector(apiRefInner, gridPageCountSelector);
    const currentPageSize = useGridSelector(apiRefInner, gridPageSizeSelector);

    return (
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr auto 1fr" },
          alignItems: "center",
          rowGap: 1,
          columnGap: 2,
          width: "100%",
          padding: "8px 16px",
          borderTop: "1px solid rgba(224, 224, 224, 1)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: { xs: "center", sm: "flex-start" },
            gap: 1,
            order: { xs: 2, sm: 1 },
          }}
        >
          <Typography sx={{ fontSize: "12px", color: "text.secondary", whiteSpace: "nowrap" }}>
            Rows per page:
          </Typography>
          <Select
            size="small"
            variant="standard"
            value={pageSizeOptions.includes(currentPageSize) ? currentPageSize : pageSizeOptions[0]}
            onChange={(event) => apiRefInner.current.setPageSize(Number(event.target.value))}
            sx={{ fontSize: "12px", minWidth: 48 }}
            MenuProps={{ PaperProps: { sx: { fontSize: "12px" } } }}
          >
            {pageSizeOptions.map((option) => (
              <MenuItem key={option} value={option} sx={{ fontSize: "12px" }}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "center", order: { xs: 1, sm: 2 } }}>
          <MuiPagination
            shape="rounded"
            count={pageCount}
            page={page + 1}
            onChange={(event, newPage) => {
              apiRefInner.current.setPage(newPage - 1);
            }}
            sx={styles.paginationStyle}
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: { xs: "center", sm: "flex-end" },
            flexWrap: "wrap",
            rowGap: 0.5,
            columnGap: 1,
            order: 3,
          }}
        >
          <Typography sx={styles.Label1FooterTable}>Total:</Typography>
          <Typography sx={styles.Label2FooterTable}>
            {totalRowCount.toLocaleString()} {totalRowCount === 1 ? "record" : "records"}
          </Typography>
          {totalAmount && (
            <Box sx={{ display: "flex", alignItems: "center", columnGap: 1 }}>
              <Typography sx={{ margin: "0px 4px", fontSize: "12px" }}>•</Typography>
              <Typography sx={styles.Label1FooterTable}>Total Amount:</Typography>
              <Typography sx={styles.Label2FooterTable}>{totalAmount}</Typography>
            </Box>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <DataGrid
      apiRef={apiRef}
      rows={props.rows}
      columns={props.columns}
      onRowDoubleClick={props.handleDoubleRowClick}
      hideFooterSelectedRowCount
      pagination
      // Instead of a fixed-height/scrolling container, the grid sizes
      // itself to however many rows are currently shown (driven by the
      // rows-per-page selector below) - it grows or shrinks with the
      // content instead of requiring an inner scrollbar.
      autoHeight
      pageSize={initialPageSize}
      pageSizeOptions={pageSizeOptions}
      // Callers can still provide their own slots (e.g. a custom
      // `noRowsOverlay`) - the pagination slot always stays the one defined
      // in this component, so every table keeps the same footer/pagination
      // look regardless of who's using it.
      slots={{ ...(props.slots || {}), pagination: CustomFooter }}
      slotProps={props.slotProps}
      initialState={{
        pagination: { paginationModel: { pageSize: initialPageSize } },
        sorting: { sortModel: props.sortConfig },
      }}
      rowHeight={70}
      density="compact"
      autosizeOptions={autosizeOptions}
      sx={[
        styles.tableStyles,
        {
          "& .MuiDataGrid-overlayWrapper": { minHeight: EMPTY_STATE_MIN_HEIGHT },
          "& .MuiDataGrid-overlayWrapperInner": { minHeight: EMPTY_STATE_MIN_HEIGHT },
        },
      ]}
    />
  );
}

export default TableComponent;